// src/screens/booking/StoreDetailScreen.js
// 78차 62차: 지점별 비활성화(branch.isDeleted) 지점은 이 화면의 지점 목록에서도 제외
// (store.branches를 직접 읽어서 flattenStoreThemes를 거치지 않으므로 별도 필터 필요).
// 32차: 리뷰 목록에 인증 배지 (verified: true) 표시, soft-delete 매장 접근 차단
// 33차: VerifiedBadge prop 방식으로 명확화
// 34차: StarRating / VerifiedBadge / ReviewCard → 공통 컴포넌트로 교체
// 51차: 색 theme.js 단일화 — 자체 색 정의 제거, COLORS 참조 / 주황·초록 통일
// 74차: 영업용 데모 매장(isDemo:true) 접근 차단 롤백 → 재도입 — stores.visibleToUid와
//       일치하는 전용 데모 고객 계정, 또는 그 매장의 매장관리자 본인만 볼 수 있음
// 77차: 화면 최상단 공백이 과하게 크고 헤더가 안 보이는 것처럼 느껴지던 버그 수정 —
// 최상위 View에 paddingTop: insets.top을 주고 그 안에 AppHeader(DetailHeader)를 또
// 렌더하고 있었는데, AppHeader 자체가 이미 내부적으로 safe-area top을 처리해서(topPad)
// 세이프에리어 여백이 중복 적용되고 있었음(체감상 헤더 위에 빈 공간이 한 번 더 생김).
// paddingTop: insets.top 3곳 전부 제거, insets/useSafeAreaInsets도 다른 용도로 안 쓰여
// 같이 제거.

import React, { useEffect, useState, useCallback } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, ScrollView, TouchableOpacity,
  StyleSheet, ActivityIndicator,
  Linking, Alert,
} from 'react-native';
// 78차 37차: 테마 썸네일이 캐싱 없이 화면 진입마다 재다운로드되던 것 — expo-image로
// 교체(디스크 캐시 자동 적용, 재방문 시 즉시 표시). getAllStores() 캐시와 짝을 이루는 성능 개선.
import { Image } from 'expo-image';
import { useNavigation, useRoute } from '@react-navigation/native';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { isConfirmedMinorBirth } from '../../utils/dateUtils';
import { flattenStoreThemes, normalizeProduct, getStoreDisplayName } from '../../services/productService';
import { getReviewsByProduct } from '../../services/reviewService';
import { StarRating, ReviewCard } from '../../components/common/ReviewCard';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader, { LogoMark } from '../../components/common/AppHeader';
import { SecondaryButton } from '../../components/common/Buttons';
import { MenuStoreIcon, LocationIcon, MenuPointsIcon, MenuNoticeIcon, PeopleIcon, IconLabel, CheckmarkIcon, PhoneIcon, EmailIcon, WarningIcon } from '../../components/common/Icons';
import { COLORS } from '../../constants/theme';
import { getGenreMeta, getDifficultyLabel, getDifficultyColor, getSeatingTypeLabel } from '../../constants';

const GOLD       = COLORS.gold;
const GOLD_BG    = COLORS.goldBg;
const GOLD_BDR   = COLORS.goldBorder;
const BG         = COLORS.bg;
const SURFACE    = COLORS.surface;
const SURFACE2   = COLORS.surface2;
const BORDER     = COLORS.border;
const TEXT       = COLORS.text;
const TEXT_SEC   = COLORS.textSec;
const TEXT_MUTED = COLORS.textMuted;
const GREEN      = COLORS.green;

// 76차: 커스텀 헤더 → 공통 AppHeader로 교체
function DetailHeader({ title }) {
  return <AppHeader showBack centerElement={<Text style={hd.title} numberOfLines={1}>{title}</Text>} />;
}
const hd = StyleSheet.create({
  title: { color: TEXT, fontSize: 15, fontWeight: '800' },
});

// ── 섹션 헤더 ─────────────────────────────────────────────────────────────────
function SectionHeader({ title, count }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 14, gap: 8 }}>
      <View style={{ width: 3, height: 16, backgroundColor: GOLD, borderRadius: 2 }} />
      <Text style={{ color: TEXT, fontSize: 15, fontWeight: '800' }}>{title}</Text>
      {count != null && (
        <View style={{
          backgroundColor: GOLD_BG, borderRadius: 10, paddingHorizontal: 8,
          paddingVertical: 2, borderWidth: 1, borderColor: GOLD_BDR,
        }}>
          <Text style={{ color: GOLD, fontSize: 11, fontWeight: '700' }}>{count}</Text>
        </View>
      )}
    </View>
  );
}

// ── 정보 행 ──────────────────────────────────────────────────────────────────
function InfoRow({ emoji, Icon, label, value, onPress, valueColor }) {
  return (
    <TouchableOpacity
      style={ir.row}
      onPress={onPress}
      disabled={!onPress}
      activeOpacity={onPress ? 0.7 : 1}>
      {Icon ? <View style={{ width: 24 }}><Icon size={15} color={TEXT_MUTED} /></View> : <Text style={ir.emoji}>{emoji}</Text>}
      <Text style={ir.label}>{label}</Text>
      <Text
        style={[ir.value, valueColor && { color: valueColor }, onPress && { textDecorationLine: 'underline' }]}
        numberOfLines={2}>
        {value}
      </Text>
    </TouchableOpacity>
  );
}
const ir = StyleSheet.create({
  row:   {
    flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: BORDER, gap: 10,
  },
  emoji: { fontSize: 16, width: 22, marginTop: 1 },
  label: { color: TEXT_MUTED, fontSize: 13, width: 60, flexShrink: 0 },
  value: { color: TEXT_SEC, fontSize: 13, flex: 1, lineHeight: 20 },
});

// ── 테마 카드 ────────────────────────────────────────────────────────────────
function ThemeCard({ theme, onPress }) {
  const [reviews,     setReviews]     = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [revLoading,  setRevLoading]  = useState(false);

  // 80차 후속: 체험형(정원제) 테마는 난이도 대신 "체험형" 배지 + 1인당 가격으로 표기
  const isShow = theme.bookingFormat === 'show';
  const minPrice = theme.pricing?.length
    ? Math.min(...theme.pricing.map(p => Number(p.price) || 0))
    : null;

  const handleToggleReviews = async () => {
    if (!showReviews && reviews.length === 0) {
      setRevLoading(true);
      try {
        const data = await getReviewsByProduct(theme.id, 10);
        setReviews(data);
      } catch (e) {
        console.warn('[ThemeCard] 리뷰 로드 실패:', e.message);
      } finally {
        setRevLoading(false);
      }
    }
    setShowReviews(v => !v);
  };

  // 33차: verified === true 인 리뷰만 카운트 (undefined/false 제외)
  const verifiedCount = reviews.filter(r => r.verified === true).length;

  return (
    <View style={tc.card}>
      <TouchableOpacity onPress={onPress} activeOpacity={0.85}>
        {/* 썸네일 */}
        <View style={tc.imgWrap}>
          {theme.thumbnailUrl || theme.imageUrl
            ? <Image source={{ uri: theme.thumbnailUrl || theme.imageUrl }} style={tc.img} contentFit="cover" />
            : <View style={[tc.img, tc.imgPlaceholder]}><LogoMark size={32} /></View>
          }
          {theme.genre ? (() => {
            const gm = getGenreMeta(theme.genre);
            return (
              <View style={[tc.genreBadge, { backgroundColor: gm.color+'33', borderColor: gm.color+'55' }]}>
                <Text style={[tc.genreBadgeTxt, { color: gm.color }]}>
                  {theme.genre}{gm.en ? ` · ${gm.en}` : ''}
                </Text>
              </View>
            );
          })() : null}
        </View>

        {/* 정보 */}
        <View style={tc.info}>
          <Text style={tc.name} numberOfLines={1}>{theme.name}</Text>

          <View style={tc.metaRow}>
            {/* 80차 후속: [정책 확정] 배지 순서 — ①형식(방탈출/체험형, 필수) → ②19금 → ③장르(이
                카드는 장르 배지가 없어 난이도 배지로 대체 표시) */}
            {isShow ? (
              <>
                <View style={[tc.metaBadge, { borderColor: COLORS.blue+'55', backgroundColor: COLORS.blue+'15' }]}>
                  <Text style={[tc.metaBadgeTxt, { color: COLORS.blue }]}>체험형</Text>
                </View>
                {/* 80차 후속: 체험형 전용 — 단독 대관/합석 뱃지 */}
                <View style={[tc.metaBadge, { borderColor: COLORS.purple+'55', backgroundColor: COLORS.purple+'15' }]}>
                  <Text style={[tc.metaBadgeTxt, { color: COLORS.purple }]}>{getSeatingTypeLabel(theme.seatingType)}</Text>
                </View>
              </>
            ) : (
              <View style={[tc.metaBadge, { borderColor: COLORS.teal+'55', backgroundColor: COLORS.teal+'15' }]}>
                <Text style={[tc.metaBadgeTxt, { color: COLORS.teal }]}>방탈출</Text>
              </View>
            )}
            {theme.isAdultOnly && (
              <View style={[tc.metaBadge, { borderColor: COLORS.danger+'55', backgroundColor: COLORS.dangerBg }]}>
                <Text style={[tc.metaBadgeTxt, { color: COLORS.danger }]}>19금</Text>
              </View>
            )}
            {!isShow && (
              <View style={[tc.metaBadge, {
                borderColor: getDifficultyColor(theme.difficulty) + '55',
                backgroundColor: getDifficultyColor(theme.difficulty) + '15',
              }]}>
                <Text style={[tc.metaBadgeTxt, { color: getDifficultyColor(theme.difficulty) }]}>
                  {getDifficultyLabel(theme.difficulty)}
                </Text>
              </View>
            )}
            <View style={tc.metaBadge}>
              <Text style={tc.metaBadgeTxt}>⏱ {theme.duration || 60}분</Text>
            </View>
            <View style={[tc.metaBadge, { flexDirection: 'row', alignItems: 'center', gap: 3 }]}>
              <PeopleIcon size={10} color={TEXT_MUTED} />
              <Text style={tc.metaBadgeTxt}>
                {theme.minPeople || 1}~{theme.maxPeople || '?'}명
              </Text>
            </View>
          </View>

          {/* 평점 + 리뷰 수 */}
          {theme.reviewCount > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <StarRating rating={Math.round(theme.rating || 0)} size={12} />
              <Text style={{ color: GOLD, fontSize: 12, fontWeight: '700' }}>
                {(theme.rating || 0).toFixed(1)}
              </Text>
              <Text style={{ color: TEXT_MUTED, fontSize: 11 }}>
                ({theme.reviewCount}개)
              </Text>
              {/* 33차: verified === true 인 리뷰 수만 표시 */}
              {verifiedCount > 0 && (
                <View style={{
                  backgroundColor: GREEN + '1F',
                  borderRadius: 6, borderWidth: 1,
                  borderColor: GREEN + '66',
                  paddingHorizontal: 7, paddingVertical: 2,
                }}>
                  <IconLabel Icon={CheckmarkIcon} size={11} color={GREEN} textStyle={{ color: GREEN, fontSize: 11, fontWeight: '700' }}>{verifiedCount}</IconLabel>
                </View>
              )}
            </View>
          )}

          {isShow ? (
            theme.pricePerPerson > 0 && <Text style={tc.price}>{Number(theme.pricePerPerson).toLocaleString()}원 (1인당)</Text>
          ) : (
            minPrice != null && minPrice > 0 && <Text style={tc.price}>{minPrice.toLocaleString()}원~</Text>
          )}
        </View>
      </TouchableOpacity>

      {/* 리뷰 토글 버튼 */}
      {theme.reviewCount > 0 && (
        <TouchableOpacity
          style={tc.reviewToggle}
          onPress={handleToggleReviews}
          activeOpacity={0.7}>
          {revLoading
            ? <ActivityIndicator size="small" color={GOLD} />
            : <Text style={tc.reviewToggleTxt}>
                {showReviews ? '▲ 리뷰 접기' : `▼ 리뷰 보기 (${theme.reviewCount}개)`}
              </Text>
          }
        </TouchableOpacity>
      )}

      {/* 리뷰 목록 */}
      {showReviews && !revLoading && (
        <View style={{ paddingHorizontal: 14, paddingBottom: 14 }}>
          {reviews.length === 0 ? (
            <Text style={{ color: TEXT_MUTED, fontSize: 12, textAlign: 'center', paddingVertical: 12 }}>
              등록된 리뷰가 없어요.
            </Text>
          ) : (
            reviews.map(r => <ReviewCard key={r.id} review={r} />)
          )}
        </View>
      )}
    </View>
  );
}

const tc = StyleSheet.create({
  card:          {
    backgroundColor: SURFACE2, borderRadius: 14, borderWidth: 1,
    borderColor: BORDER, overflow: 'hidden', marginBottom: 12,
  },
  imgWrap:       { width: '100%', height: 160, position: 'relative' },
  img:           { width: '100%', height: '100%' },
  imgPlaceholder:{ backgroundColor: '#0d0d1f', justifyContent: 'center', alignItems: 'center' },
  genreBadge:    {
    position: 'absolute', top: 10, right: 10,
    borderWidth: 1, borderRadius: 6,
    paddingHorizontal: 8, paddingVertical: 3,
  },
  genreBadgeTxt: { fontSize: 11, fontWeight: '700' },
  info:          { padding: 14 },
  name:          { color: TEXT, fontSize: 15, fontWeight: '800', marginBottom: 8 },
  metaRow:       { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8 },
  metaBadge:     {
    backgroundColor: SURFACE, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3,
    borderWidth: 1, borderColor: BORDER,
  },
  metaBadgeTxt:  { color: TEXT_MUTED, fontSize: 11, fontWeight: '600' },
  price:         { color: GOLD, fontSize: 14, fontWeight: '800' },
  reviewToggle:  {
    borderTopWidth: 1, borderTopColor: BORDER,
    paddingVertical: 11, alignItems: 'center',
  },
  reviewToggleTxt: { color: GOLD, fontSize: 13, fontWeight: '600' },
});

// ── 지점 섹션 ────────────────────────────────────────────────────────────────
function BranchSection({ branch, themes, onThemePress }) {
  const [open, setOpen] = useState(true);

  return (
    <View style={bs.wrap}>
      <TouchableOpacity style={bs.header} onPress={() => setOpen(v => !v)} activeOpacity={0.8}>
        <View style={{ flex: 1 }}>
          <IconLabel Icon={MenuStoreIcon} size={13} color={TEXT} textStyle={bs.name}>{branch.branchName || '본점'}</IconLabel>
          {/* 78차 48차: 저장 필드명은 address가 아니라 addressDetail — 항상 undefined라
              city/district(구 단위)까지만 노출되던 버그 */}
          {(branch.addressDetail || branch.city || branch.district) ? (
            <IconLabel Icon={LocationIcon} size={11} color={TEXT_MUTED} textStyle={bs.address} numberOfLines={1}>
              {[branch.city, branch.district, branch.addressDetail].filter(Boolean).join(' ')}
            </IconLabel>
          ) : null}
        </View>
        <View style={bs.countBadge}>
          <Text style={bs.countTxt}>테마 {themes.length}</Text>
        </View>
        <Text style={bs.chevron}>{open ? '▲' : '▽'}</Text>
      </TouchableOpacity>

      {open && (
        <View style={bs.themeList}>
          {themes.map((theme, i) => (
            <ThemeCard key={i} theme={theme} onPress={() => onThemePress(theme)} />
          ))}
        </View>
      )}
    </View>
  );
}
const bs = StyleSheet.create({
  wrap:       { marginBottom: 20 },
  header:     {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: SURFACE2, borderRadius: 12, padding: 14,
    borderWidth: 1, borderColor: BORDER, marginBottom: 10,
  },
  name:       { color: TEXT, fontSize: 14, fontWeight: '800', marginBottom: 3 },
  address:    { color: TEXT_MUTED, fontSize: 12 },
  countBadge: {
    backgroundColor: GOLD_BG, borderRadius: 8, paddingHorizontal: 8,
    paddingVertical: 3, borderWidth: 1, borderColor: GOLD_BDR,
  },
  countTxt:   { color: GOLD, fontSize: 11, fontWeight: '700' },
  chevron:    { color: TEXT_MUTED, fontSize: 14 },
  themeList:  { paddingLeft: 4 },
});

// ── 운영 시간 파서 ────────────────────────────────────────────────────────────
function parseOperatingHours(store) {
  if (store.operatingHours) return store.operatingHours;
  const branch = store.branches?.[0];
  if (branch?.startTime && branch?.endTime) {
    return `${branch.startTime} ~ ${branch.endTime}`;
  }
  return null;
}

// ── 메인 ─────────────────────────────────────────────────────────────────────
export default function StoreDetailScreen() {
  const navigation = useNavigation();
  const route      = useRoute();
  const { profile } = useAuth();

  const storeId = route.params?.storeId || route.params?.product?.storeId;

  const [store,   setStore]   = useState(null);
  const [themes,  setThemes]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [storeEvents, setStoreEvents] = useState([]); // 78차 62차: 이 매장에 연결된 공지(포스터) 목록

  useEffect(() => {
    if (!storeId) { setError('매장 정보를 찾을 수 없습니다.'); setLoading(false); return; }

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'stores', storeId));
        if (!snap.exists()) { setError('매장이 존재하지 않습니다.'); return; }
        const data = { id: snap.id, ...snap.data() };
        // 32차: soft-delete된 매장 접근 차단
        if (data.isDeleted === true) { setError('운영이 종료된 매장입니다.'); return; }
        // 80차 후속: [정책 변경] 데모 매장 전용계정 게이트 제거 — 완전 공개로 전환(사용자 확인).
        setStore(data);
        // 80차 후속: 확인된 미성년자에게 19금 테마 숨김 — 이 화면은 getAllProducts를
        // 안 거치고(flattenStoreThemes 직접 호출) isVisibleToViewer 필터를 안 타므로
        // 여기서 별도로 적용.
        const isMinor = isConfirmedMinorBirth(profile?.birth);
        setThemes(flattenStoreThemes([data]).filter(t => !(t.isAdultOnly && isMinor)));
      } catch (e) {
        console.error('[StoreDetail]', e);
        setError('매장 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    })();
  }, [storeId, profile?.birth]);

  // 78차 62차: 총괄관리자가 이 매장에 연결해 등록한 공지(포스터 이미지 첨부) — 만료 안 된 것만.
  // EventsScreen.js의 만료 필터(!expiresAt || expiresAt > now)와 동일 기준.
  useEffect(() => {
    if (!storeId) return;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'notices'), where('storeId', '==', storeId)));
        const nowIso = new Date().toISOString();
        const active = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(n => n.posterImageUrl && (!n.expiresAt || n.expiresAt > nowIso));
        setStoreEvents(active);
      } catch (e) {
        setStoreEvents([]);
      }
    })();
  }, [storeId]);

  const handleCall = useCallback((contact) => {
    if (!contact) return;
    const tel = `tel:${contact.replace(/[^0-9]/g, '')}`;
    Linking.openURL(tel).catch(() =>
      showAlert('오류', '전화 앱을 열 수 없습니다.')
    );
  }, []);

  const handleThemePress = useCallback((theme) => {
    navigation.navigate('ThemeDetail', { product: normalizeProduct(theme) });
  }, [navigation]);

  const storeName = getStoreDisplayName(store, route.params?.storeName || '매장 상세');
  const opHours   = store ? parseOperatingHours(store) : null;
  // 78차 62차: 지점별 비활성화(branch.isDeleted) — 이 화면은 store.branches를 직접
  // 읽어서(flattenStoreThemes를 안 거침) 비활성 지점을 따로 걸러줘야 함
  // 79차: [버그 수정] .filter()가 배열 인덱스를 0부터 재부여하는데, themes(flattenStoreThemes가
  // 만듦)의 t.branchIdx는 원본 store.branches 배열의 실제 위치를 가리켜서 두 인덱스
  // 체계가 어긋나 있었음(전수 정독으로 발견) — 지점이 [A(소프트삭제),B,C]면 필터된
  // 배열은 [B,C](새 인덱스 0,1)인데, 아래 다중지점 렌더에서 "인덱스 0(B) 헤더"에
  // themes.filter(branchIdx===0)을 걸면 원본 인덱스 0인 A(테마 없음)를 찾아 B 섹션이
  // 안 보이고, "인덱스 1(C) 헤더"엔 원본 인덱스 1인 B의 테마가 표시되는 식으로 고객이
  // 보는 지점명/주소와 실제 테마가 어긋났음. 원본 인덱스를 __branchIdx로 보존.
  const branches  = (store?.branches || [])
    .map((b, bi) => (b.isDeleted === true ? null : { ...b, __branchIdx: bi }))
    .filter(Boolean);

  if (loading) return (
    <View style={{ flex: 1, backgroundColor: BG, justifyContent: 'center', alignItems: 'center' }}>
      <DetailHeader title="매장 상세" />
      <ActivityIndicator size="large" color={GOLD} style={{ marginTop: 60 }} />
    </View>
  );

  if (error) return (
    <View style={{ flex: 1, backgroundColor: BG }}>
      <DetailHeader title="매장 상세" />
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
        <View style={{ marginBottom: 16 }}><WarningIcon size={48} /></View>
        <Text style={{ color: TEXT_SEC, fontSize: 14, textAlign: 'center' }}>{error}</Text>
        <SecondaryButton label="돌아가기" onPress={() => navigation.goBack()} style={{ marginTop: 24 }} />
      </View>
    </View>
  );

  return (
    <View style={{ flex: 1, backgroundColor: BG }}>
      <DetailHeader title={storeName} />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>

        {/* ── 히어로 배너 ── */}
        <View style={s.hero}>
          <View style={s.heroInner}>
            <View style={{ marginBottom: 10 }}><MenuStoreIcon size={40} color={GOLD} /></View>
            <Text style={s.heroName}>{storeName}</Text>
            {(store?.city || store?.branches?.[0]?.city) && (
              <IconLabel Icon={LocationIcon} size={13} color={TEXT_MUTED} textStyle={s.heroLocation} style={{ justifyContent: 'center' }}>
                {store?.city || store?.branches?.[0]?.city}
                {(store?.district || store?.branches?.[0]?.district)
                  ? ` · ${store?.district || store?.branches?.[0]?.district}` : ''}
              </IconLabel>
            )}
            <View style={s.heroStatRow}>
              <View style={s.heroStat}>
                <Text style={s.heroStatNum}>{themes.length}</Text>
                <Text style={s.heroStatLabel}>테마</Text>
              </View>
              <View style={s.heroStatDivider} />
              <View style={s.heroStat}>
                <Text style={s.heroStatNum}>{branches.length}</Text>
                <Text style={s.heroStatLabel}>지점</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={s.body}>
          {/* ── 매장 정보 ── */}
          <View style={s.section}>
            <SectionHeader title="매장 정보" />
            <View style={s.infoCard}>
              {store?.contact && (
                <InfoRow
                  Icon={PhoneIcon} label="연락처" value={store.contact}
                  onPress={() => handleCall(store.contact)} valueColor={GOLD}
                />
              )}
              {store?.email && (
                <InfoRow Icon={EmailIcon} label="이메일" value={store.email} />
              )}
              {opHours && (
                <InfoRow Icon={MenuPointsIcon} label="운영시간" value={opHours} />
              )}
              {store?.notice && (
                <InfoRow Icon={MenuNoticeIcon} label="공지" value={store.notice} />
              )}
              {/* 79차: [버그 수정] 78차48차에 BranchSection(위)은 이미 고쳤는데 이 "매장 정보"
                  카드 안의 같은 정보를 보여주는 블록은 안 고쳐서, 저장 필드명이 address가
                  아니라 city/district/addressDetail이라 이 지점별 주소 행 자체가 항상 안
                  보이고 있었음(형제 코드 중 하나만 고친 패턴, 테마/매장 도메인 재정독
                  fork로 발견) — 동일한 조합 방식으로 교체. */}
              {branches.map((branch, i) => {
                const addr = [branch.city, branch.district, branch.addressDetail].filter(Boolean).join(' ');
                return addr ? (
                  <InfoRow
                    key={i} Icon={LocationIcon}
                    label={branch.branchName || `지점 ${i + 1}`}
                    value={addr}
                  />
                ) : null;
              })}
              {!store?.contact && !store?.email && !opHours && !store?.notice &&
               branches.every(b => !b.city && !b.district && !b.addressDetail) && (
                <Text style={{ color: TEXT_MUTED, fontSize: 13, paddingVertical: 8 }}>
                  등록된 매장 정보가 없습니다.
                </Text>
              )}
            </View>
          </View>

          {/* 78차 62차: 이벤트 — 총괄관리자가 이 매장에 연결해 등록한 포스터.
              등록된 게 하나도 없으면 섹션 자체를 숨김(빈 헤더로 안 보이게). */}
          {storeEvents.length > 0 && (
            <View style={s.section}>
              <SectionHeader title="이벤트" count={storeEvents.length} />
              {storeEvents.map(ev => {
                const content = (
                  <Image source={{ uri: ev.posterImageUrl }} style={s.eventPoster} contentFit="contain" />
                );
                return ev.link ? (
                  <TouchableOpacity key={ev.id} onPress={() => Linking.openURL(ev.link).catch(() => {})} activeOpacity={0.85}>
                    {content}
                  </TouchableOpacity>
                ) : <View key={ev.id}>{content}</View>;
              })}
            </View>
          )}

          {/* ── 테마 목록 ── */}
          <View style={s.section}>
            <SectionHeader title="테마 목록" count={themes.length} />

            {themes.length === 0 ? (
              <View style={s.emptyTheme}>
                <View style={{ marginBottom: 10 }}><LogoMark size={40} /></View>
                <Text style={{ color: TEXT_SEC, fontSize: 14, fontWeight: '700' }}>
                  등록된 테마가 없습니다
                </Text>
              </View>
            ) : branches.length === 1 ? (
              themes.map((theme, i) => (
                <ThemeCard key={i} theme={theme} onPress={() => handleThemePress(theme)} />
              ))
            ) : (
              branches.map((branch) => {
                const branchThemes = themes.filter(t => t.branchIdx === branch.__branchIdx);
                if (!branchThemes.length) return null;
                return (
                  <BranchSection
                    key={branch.__branchIdx}
                    branch={branch}
                    themes={branchThemes}
                    onThemePress={handleThemePress}
                  />
                );
              })
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  hero:            {
    backgroundColor: SURFACE, borderBottomWidth: 1, borderBottomColor: BORDER,
    paddingVertical: 28, paddingHorizontal: 20, alignItems: 'center',
  },
  heroInner:       { alignItems: 'center' },
  heroName:        {
    color: TEXT, fontSize: 22, fontWeight: '900', letterSpacing: 0.4,
    textAlign: 'center', marginBottom: 4,
  },
  heroLocation:    { color: TEXT_MUTED, fontSize: 13, marginBottom: 16 },
  heroStatRow:     { flexDirection: 'row', alignItems: 'center', gap: 20 },
  heroStat:        { alignItems: 'center', gap: 2 },
  heroStatNum:     { color: GOLD, fontSize: 20, fontWeight: '900' },
  heroStatLabel:   { color: TEXT_MUTED, fontSize: 11 },
  heroStatDivider: { width: 1, height: 28, backgroundColor: BORDER },
  body:            { padding: 20 },
  section:         { marginBottom: 28 },
  eventPoster:     { width: '100%', aspectRatio: 3/4, borderRadius: 14, backgroundColor: SURFACE2, marginBottom: 12 },
  infoCard:        {
    backgroundColor: SURFACE2, borderRadius: 14, paddingHorizontal: 16,
    borderWidth: 1, borderColor: BORDER,
  },
  emptyTheme:      {
    backgroundColor: SURFACE2, borderRadius: 14, paddingVertical: 40,
    alignItems: 'center', borderWidth: 1, borderColor: BORDER,
  },
});